import { useState } from 'react';

interface Emotion {
  name: string;
  cardTitle: string;
  image: string;
  description: string;
  artist: string;
  artwork: string;
  year: string;
}

export default function HeroSection() {
  const [selectedEmotion, setSelectedEmotion] = useState<Emotion | null>(null);

  const emotions: Emotion[] = [
    {
      name: 'Surprise',
      cardTitle: 'Surprise',
      image: 'https://storage.helloreaddy.io/project_files/b4f3eeda-f893-4987-8921-4636c63c4dc6/defb3690-faf2-4bd2-a53b-654077c7cd44_compressed_Surprise-White.webp',
      description: 'Surprise is a rapid orienting emotion that appears when something unexpected happens. Somatically it may feel like a quick inhale, widened eyes, or a sudden jolt of alertness in the body. It signals needs for updated information, clarity, and reorientation to a changed situation.',
      artist: 'Gustave Courbet',
      artwork: 'The Desperate Man',
      year: '1843–1845'
    },
    {
      name: 'Peace',
      cardTitle: 'Peace',
      image: 'https://storage.helloreaddy.io/project_files/b4f3eeda-f893-4987-8921-4636c63c4dc6/47af46aa-a236-4cb5-8e83-d4cd94a67389_compressed_Orange-Peaceful.webp',
      description: 'Peace is a grounded state of regulation and inner balance. In the body it shows up as steady breath, relaxed muscles, and a sense of spaciousness. It signals that needs for safety, clarity, and stability are met.',
      artist: 'Frederic Leighton',
      artwork: 'Flaming June',
      year: '1895'
    },
    {
      name: 'Anger',
      cardTitle: 'Anger',
      image: 'https://storage.helloreaddy.io/project_files/b4f3eeda-f893-4987-8921-4636c63c4dc6/a6491032-d153-4f9d-9667-8b1b4e80e8e7_compressed_Anger-red.webp',
      description: 'Anger arises when a boundary is crossed or when something feels unjust. Somatically it may feel like heat, tightness, or mobilizing energy in the arms, jaw, or chest. It signals needs for protection, respect, fairness, or assertive action.',
      artist: 'Nicholas Roerich',
      artwork: 'The Fiery Face',
      year: '1924'
    },
    {
      name: 'Fear',
      cardTitle: 'Fear',
      image: 'https://storage.helloreaddy.io/project_files/b4f3eeda-f893-4987-8921-4636c63c4dc6/cf5802d8-36c3-4115-87b4-977aa1aa5328_compressed_the-scream.webp',
      description: 'Fear appears when you sense a threat to your safety, wellbeing, or something you value. Somatic experience may include a racing heart, shallow breath, tense muscles, and an urge to freeze, flee, or protect yourself. It often points to needs for safety, protection, and reassurance.',
      artist: 'Edvard Munch',
      artwork: 'The Scream',
      year: '1893'
    },
    {
      name: 'Happiness',
      cardTitle: 'Happiness',
      image: 'https://storage.helloreaddy.io/project_files/b4f3eeda-f893-4987-8921-4636c63c4dc6/dab69062-830f-491c-839f-cf85b2fb23d6_compressed_Happy-Yellow.webp',
      description: 'Happiness is a sustained sense of wellbeing that comes from living in alignment with your values and experiencing meaningful moments. In the body it may feel like warmth, ease, and a relaxed, open posture. It often signals that needs for connection, fulfillment, balance, or purpose are being met.',
      artist: 'Gustav Klimt',
      artwork: 'The Kiss',
      year: '1907–1908'
    },
    {
      name: 'Sadness',
      cardTitle: 'Sadness',
      image: 'https://storage.helloreaddy.io/project_files/b4f3eeda-f893-4987-8921-4636c63c4dc6/068c0901-c5a3-48d4-ab09-4da6c1075f17_compressed_Sad-Green.webp',
      description: 'Sadness appears when you lose something meaningful or when a need goes unmet for a long time. Somatic experience may include heaviness in the chest, slower breath, and inward, collapsing sensations. It often points to needs for comfort, support, rest, or letting go.',
      artist: 'John Everett Millais',
      artwork: 'Ophelia',
      year: '1851–1852'
    }
  ];

  const scrollToBooking = () => {
    const element = document.getElementById('services-pricing');
    if (element) {
      const headerHeight = 80;
      const elementPosition = element.offsetTop - headerHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  const scrollToDrawingInsight = () => {
    const element = document.getElementById('drawing-insight');
    if (element) {
      const headerHeight = 80;
      const elementPosition = element.offsetTop - headerHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  const openModal = (emotion: Emotion) => {
    setSelectedEmotion(emotion);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedEmotion(null);
    document.body.style.overflow = 'auto';
  };

  return (
    <>
      <section 
        id="home" 
        className="relative w-full pt-4 pb-4 bg-white"
      >
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          {/* Desktop: Two-column layout (lg and above) */}
          <div className="hidden lg:flex items-center gap-[60px]">
            {/* Left Column — logo, subtitle, buttons, centered */}
            <div className="flex-1 flex flex-col items-center justify-center py-8">
              <img
                src="https://storage.helloreaddy.io/project_files/b4f3eeda-f893-4987-8921-4636c63c4dc6/c7b8e138-f672-4cfc-bd50-d52cc238e3f8_compressed_Logo-Color.webp"
                alt="Yuvati — creativity becomes therapy"
                className="w-full max-w-md mb-6"
              />
              <h1 className="text-[#312921]/85 text-lg lg:text-xl max-w-xl leading-snug mb-6 text-center">
                Art Therapy · Online Worldwide &amp; In-Person in Chiang Mai.
              </h1>
              <div className="flex items-center gap-4">
                <button
                  onClick={scrollToBooking}
                  className="bg-[#D6794E] hover:bg-[#385B47] text-white px-10 py-4 rounded-xl text-lg font-normal transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center shadow-lg w-48 justify-center"
                >
                  Book a Session
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
                <button
                  onClick={scrollToDrawingInsight}
                  className="bg-[#81998C] hover:bg-[#385B47] text-white px-10 py-4 rounded-xl text-lg font-normal transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center w-48 justify-center"
                >
                  Try for Free
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
            </div>

            {/* Right Column — wheel, centered */}
            <div className="flex-1 flex flex-col items-center justify-center py-8">
              <div className="relative w-full max-w-[560px] aspect-square animate-wheel-breathe">
                {/* Pulsing concentric rings around the wheel */}
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <span className="absolute w-[90%] h-[90%] rounded-full animate-ring-pulse">
                    <span className="absolute inset-0 rounded-full border-2 border-current animate-color-sync"></span>
                  </span>
                  <span className="absolute w-[90%] h-[90%] rounded-full animate-ring-pulse" style={{ animationDelay: '1.5s' }}>
                    <span className="absolute inset-0 rounded-full border-2 border-current animate-color-sync"></span>
                  </span>
                </div>
                <svg 
                  viewBox="0 0 400 400" 
                  className="w-full h-full"
                  preserveAspectRatio="xMidYMid meet"
                >
                  <defs>
                    {emotions.map((emotion, index) => {
                      const angle = (360 / emotions.length) * index;
                      const nextAngle = (360 / emotions.length) * (index + 1);
                      const startRad = (angle - 90) * (Math.PI / 180);
                      const endRad = (nextAngle - 90) * (Math.PI / 180);
                      
                      const x1 = 200 + 180 * Math.cos(startRad);
                      const y1 = 200 + 180 * Math.sin(startRad);
                      const x2 = 200 + 180 * Math.cos(endRad);
                      const y2 = 200 + 180 * Math.sin(endRad);

                      const largeArcFlag = 0;

                      const pathData = `M 200 200 L ${x1} ${y1} A 180 180 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

                      return (
                        <clipPath key={`clip-${index}`} id={`slice-${index}`}>
                          <path d={pathData} />
                        </clipPath>
                      );
                    })}
                  </defs>

                  {emotions.map((emotion, index) => {
                    const angle = (360 / emotions.length) * index;
                    const nextAngle = (360 / emotions.length) * (index + 1);
                    const midAngle = (angle + nextAngle) / 2;
                    const midRad = (midAngle - 90) * (Math.PI / 180);
                    
                    const textX = 200 + 120 * Math.cos(midRad);
                    const textY = 200 + 120 * Math.sin(midRad);

                    return (
                      <g key={index}>
                        <image
                          href={emotion.image}
                          x="20"
                          y="20"
                          width="360"
                          height="360"
                          clipPath={`url(#slice-${index})`}
                          preserveAspectRatio="xMidYMid slice"
                          className="transition-all duration-300 cursor-pointer hover:opacity-90"
                          style={{ transformOrigin: '200px 200px' }}
                          onClick={() => openModal(emotion)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.04)';
                            e.currentTarget.style.filter = 'drop-shadow(0 8px 16px rgba(0,0,0,0.3))';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.filter = 'none';
                          }}
                          role="button"
                          aria-label={`${emotion.name} emotion`}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openModal(emotion);
                            }
                          }}
                        />
                        
                        <text
                          x={textX}
                          y={textY}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          className="pointer-events-none select-none"
                          style={{ 
                            fontFamily: "'Belanosima', sans-serif",
                            fontSize: '18px',
                            fontWeight: '400',
                            fill: '#FFFFFF',
                            textShadow: '2px 2px 8px rgba(0,0,0,0.8)',
                            filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.9))'
                          }}
                        >
                          {emotion.name}
                        </text>
                      </g>
                    );
                  })}

                  {/* Solid white circle behind center text */}
                  <circle
                    cx="200"
                    cy="200"
                    r="36"
                    fill="#FFFFFF"
                    className="pointer-events-none"
                  />

                  {/* Center Text: "Tap a / feeling" */}
                  <text
                    x="200"
                    y="192"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="currentColor"
                    className="pointer-events-none select-none animate-color-sync"
                    style={{ 
                      fontFamily: "'Belanosima', sans-serif",
                      fontSize: '15px',
                      fontWeight: '400'
                    }}
                  >
                    Tap a
                  </text>
                  <text
                    x="200"
                    y="208"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="currentColor"
                    className="pointer-events-none select-none animate-color-sync"
                    style={{ 
                      fontFamily: "'Belanosima', sans-serif",
                      fontSize: '15px',
                      fontWeight: '400'
                    }}
                  >
                    feeling
                  </text>
                </svg>
              </div>
            </div>
          </div>

          {/* Mobile: Single column (below lg) */}
          <div className="block lg:hidden w-full">
            <div className="flex flex-col items-center py-4">
              <img
                src="https://storage.helloreaddy.io/project_files/b4f3eeda-f893-4987-8921-4636c63c4dc6/c7b8e138-f672-4cfc-bd50-d52cc238e3f8_compressed_Logo-Color.webp"
                alt="Yuvati — creativity becomes therapy"
                className="w-full max-w-[280px] mb-4"
              />
              <p className="text-[#312921]/85 text-sm px-2 text-center mb-4">
                Art Therapy · Online Worldwide &amp; In-Person in Chiang Mai.
              </p>
              <div className="flex flex-col sm:flex-row items-stretch gap-3 mb-4 w-full">
                <button
                  onClick={scrollToBooking}
                  className="bg-[#D6794E] hover:bg-[#385B47] text-white px-8 py-3 rounded-xl text-base font-normal transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center shadow-lg w-full sm:flex-1 justify-center"
                >
                  Book a Session
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
                <button
                  onClick={scrollToDrawingInsight}
                  className="bg-[#81998C] hover:bg-[#385B47] text-white px-8 py-3 rounded-xl text-base font-normal transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center w-full sm:flex-1 justify-center"
                >
                  Try for Free
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
              <div className="relative w-[96vw] max-w-[96vh] aspect-square animate-wheel-breathe">
                {/* Pulsing concentric rings around the wheel */}
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <span className="absolute w-[90%] h-[90%] rounded-full animate-ring-pulse">
                    <span className="absolute inset-0 rounded-full border-2 border-current animate-color-sync"></span>
                  </span>
                  <span className="absolute w-[90%] h-[90%] rounded-full animate-ring-pulse" style={{ animationDelay: '1.5s' }}>
                    <span className="absolute inset-0 rounded-full border-2 border-current animate-color-sync"></span>
                  </span>
                </div>
                <svg 
                  viewBox="0 0 400 400" 
                  className="w-full h-full"
                  preserveAspectRatio="xMidYMid meet"
                >
                  <defs>
                    {emotions.map((emotion, index) => {
                      const angle = (360 / emotions.length) * index;
                      const nextAngle = (360 / emotions.length) * (index + 1);
                      const startRad = (angle - 90) * (Math.PI / 180);
                      const endRad = (nextAngle - 90) * (Math.PI / 180);
                      
                      const x1 = 200 + 180 * Math.cos(startRad);
                      const y1 = 200 + 180 * Math.sin(startRad);
                      const x2 = 200 + 180 * Math.cos(endRad);
                      const y2 = 200 + 180 * Math.sin(endRad);

                      const largeArcFlag = 0;

                      const pathData = `M 200 200 L ${x1} ${y1} A 180 180 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

                      return (
                        <clipPath key={`clip-mobile-${index}`} id={`slice-mobile-${index}`}>
                          <path d={pathData} />
                        </clipPath>
                      );
                    })}
                  </defs>

                  {emotions.map((emotion, index) => {
                    const angle = (360 / emotions.length) * index;
                    const nextAngle = (360 / emotions.length) * (index + 1);
                    const midAngle = (angle + nextAngle) / 2;
                    const midRad = (midAngle - 90) * (Math.PI / 180);
                    
                    const textX = 200 + 110 * Math.cos(midRad);
                    const textY = 200 + 110 * Math.sin(midRad);

                    return (
                      <g key={index}>
                        <image
                          href={emotion.image}
                          x="20"
                          y="20"
                          width="360"
                          height="360"
                          clipPath={`url(#slice-mobile-${index})`}
                          preserveAspectRatio="xMidYMid slice"
                          className="cursor-pointer active:opacity-90"
                          onClick={() => openModal(emotion)}
                          role="button"
                          aria-label={`${emotion.name} emotion`}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openModal(emotion);
                            }
                          }}
                        />
                        
                        <text
                          x={textX}
                          y={textY}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          className="pointer-events-none select-none"
                          style={{ 
                            fontFamily: "'Belanosima', sans-serif",
                            fontSize: '22px',
                            fontWeight: '400',
                            fill: '#FFFFFF',
                            textShadow: '2px 2px 8px rgba(0,0,0,0.8)',
                            filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.9))'
                          }}
                        >
                          {emotion.name}
                        </text>
                      </g>
                    );
                  })}

                  {/* Solid white circle behind center text */}
                  <circle
                    cx="200"
                    cy="200"
                    r="36"
                    fill="#FFFFFF"
                    className="pointer-events-none"
                  />

                  {/* Center Text: "Tap a / feeling" */}
                  <text
                    x="200"
                    y="192"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="currentColor"
                    className="pointer-events-none select-none animate-color-sync"
                    style={{ 
                      fontFamily: "'Belanosima', sans-serif",
                      fontSize: '13px',
                      fontWeight: '400'
                    }}
                  >
                    Tap a
                  </text>
                  <text
                    x="200"
                    y="208"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="currentColor"
                    className="pointer-events-none select-none animate-color-sync"
                    style={{ 
                      fontFamily: "'Belanosima', sans-serif",
                      fontSize: '13px',
                      fontWeight: '400'
                    }}
                  >
                    feeling
                  </text>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Full-Screen Modal - No Scroll, Auto-Scaled Typography */}
      {selectedEmotion && (
        <div 
          className="fixed inset-0 bg-[#385B47]/95 z-[9999] flex items-center justify-center p-4"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <div 
            className={`relative w-full max-w-5xl flex flex-col ${selectedEmotion && (selectedEmotion.name === 'Anger' || selectedEmotion.name === 'Sadness') ? 'lg:h-auto' : 'h-full'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              className="absolute top-2 right-2 md:top-4 md:right-4 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-[#385B47] rounded-full text-white hover:bg-[#2F4C3B] transition-all cursor-pointer z-10"
              aria-label="Close modal"
            >
              <i className="ri-close-line text-xl md:text-2xl"></i>
            </button>

            <div className={`bg-[#DBCEC5] rounded-2xl overflow-hidden flex flex-col ${selectedEmotion && (selectedEmotion.name === 'Anger' || selectedEmotion.name === 'Sadness') ? 'lg:h-auto' : 'h-full'}`}>
              {/* Top Section - Full-width white background (image + caption) */}
              <div className="bg-white">
              {/* Image Section - Fixed Height */}
              <div className={`relative w-full flex-shrink-0 h-[clamp(200px,40vh,400px)] lg:mx-auto ${selectedEmotion.name === 'Fear' ? 'lg:h-[520px] lg:max-w-[400px]' : selectedEmotion.name === 'Anger' || selectedEmotion.name === 'Sadness' ? 'lg:h-auto lg:aspect-[4/3] lg:max-w-xl' : 'lg:h-[520px] lg:max-w-xl'}`}>
                <img
                  src={selectedEmotion.image}
                  alt={selectedEmotion.name}
                  className="w-full h-full object-contain bg-white"
                />
              </div>

              {/* Artwork Caption */}
              <div className="px-4 py-3 md:px-8 md:py-4 border-b border-gray-100 bg-white">
                <p 
                  className="text-[#312921]/85 text-center"
                  style={{
                    fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
                    lineHeight: '1.5'
                  }}
                >
                  {selectedEmotion.artist} — <em>{selectedEmotion.artwork}</em>, {selectedEmotion.year}
                </p>
              </div>
              </div>

              {/* Text Section - Flexible with Auto-Scaled Typography */}
              <div className="flex-1 flex flex-col justify-start px-4 py-3 md:px-8 md:py-4 overflow-hidden">
                <h2 
                  id="modal-title"
                  className="font-normal mb-2 md:mb-4 cinematic-text-dark text-center"
                  style={{ 
                    fontSize: 'clamp(1.5rem, 4vw, 3rem)',
                    lineHeight: '1.2'
                  }}
                >
                  {selectedEmotion.cardTitle}
                </h2>
                <p 
                  className="text-[#312921]/85"
                  style={{
                    fontSize: 'clamp(0.875rem, 2vw, 1.125rem)',
                    lineHeight: 'clamp(1.4, 1.5, 1.6)'
                  }}
                >
                  {selectedEmotion.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}